import {
	SnowAttribute,
	TaskDefinition,
	WorkflowDefinition,
	WorkflowDefinitionNode,
} from '@amzn/snow-nxt-workflow-service-typescript-client';
import { EntityAttributes, SNOWEntity } from 'src/@types/snowDataServiceTypes/createEntity.interface';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import {
	isEmptyAttributeValue,
	isTaskSpecificAttribute,
	isWorkflowSpecificAttribute,
} from 'src/utils/attributeTypeUtils';

/** The tasks of one sub-workflow, which get a task-specific step of their own. */
export interface SubWorkflowTaskGroup {
	workflowId: string;
	workflowName: string;
	workflowDefinition?: WorkflowDefinition;
	taskDefinitions: TaskDefinition[];
}

export interface WorkflowTreeTasks {
	/** The parent's own tasks. */
	taskDefinitions: TaskDefinition[];
	/** One group per sub-workflow, at any depth, in the order reached. */
	subWorkflowGroups: SubWorkflowTaskGroup[];
	/** The parent's tasks and every group's tasks, for what spans the whole workflow. */
	allTaskDefinitions: TaskDefinition[];
}

const isSubWorkflowTask = (task: TaskDefinition) => task.taskType === SUB_WORKFLOW_TASK_TYPE;

// Gives each empty input of the task that shouldFill picks the value findValue returns for it, if any.
const fillEmptyInputs = (
	task: TaskDefinition,
	shouldFill: (input: SnowAttribute) => boolean,
	findValue: (input: SnowAttribute) => SnowAttribute['value']
): TaskDefinition => ({
	...task,
	taskInputs: (task.taskInputs ?? []).map((input) => {
		if (!shouldFill(input) || !isEmptyAttributeValue(input.value)) return input;

		const value = findValue(input);

		return isEmptyAttributeValue(value) ? input : { ...input, value };
	}),
});

// The value of each workflow-level input the tasks declare, by name: what the requester entered,
// else the value the input already carries. The first one found wins; empty ones are left out.
const getWorkflowLevelValues = (
	tasks: TaskDefinition[],
	entered: Record<string, unknown> = {}
): Record<string, unknown> => {
	const values: Record<string, unknown> = {};

	tasks
		.flatMap((task) => task.taskInputs ?? [])
		.filter(isWorkflowSpecificAttribute)
		.forEach(({ attributeName, value }: SnowAttribute) => {
			const resolved = entered[attributeName!] ?? value;

			if (!(attributeName! in values) && !isEmptyAttributeValue(resolved)) {
				values[attributeName!] = resolved;
			}
		});

	return values;
};

/**
 * One group per sub-workflow, at any depth. A task-specific input takes the value that the
 * immediate parent's SubWorkflow task supplies for it as `<childTaskId>.<attributeName>`.
 */
const getSubWorkflowGroups = (
	parentTasks: TaskDefinition[],
	subWorkflows: WorkflowDefinitionNode[]
): SubWorkflowTaskGroup[] =>
	subWorkflows.flatMap((node) => {
		const { workflowId = '', workflowDefinitionName } = node.workflowDefinition ?? {};
		const embeddingTask = parentTasks.find(
			(task) => isSubWorkflowTask(task) && task.childWorkflowId === workflowId
		);
		const tasks = node.taskDefinitions ?? [];

		return [
			{
				workflowId,
				workflowName: workflowDefinitionName || workflowId,
				workflowDefinition: node.workflowDefinition,
				taskDefinitions: tasks.map((task) =>
					fillEmptyInputs(task, isTaskSpecificAttribute, (input) =>
						embeddingTask?.taskInputs?.find(
							(supplied) => supplied.attributeName === `${task.taskId}.${input.attributeName}`
						)?.value
					)
				),
			},
			...getSubWorkflowGroups(tasks, node.subWorkflows ?? []),
		];
	});

/**
 * Splits a workflow's tasks into the parent's own and one group per embedded sub-workflow, at
 * any depth, so the form can show each sub-workflow's task-specific inputs on a step of its own.
 *
 * An empty workflow-level input takes the value found for it anywhere in the tree, looking at the
 * parent first. SubWorkflow tasks only carry values for the workflow they embed, so they are left
 * out once those values are copied.
 */
export const withSubWorkflowInputs = (
	taskDefinitions: TaskDefinition[],
	subWorkflows: WorkflowDefinitionNode[] = []
): WorkflowTreeTasks => {
	if (!subWorkflows.length) {
		return { taskDefinitions, subWorkflowGroups: [], allTaskDefinitions: taskDefinitions };
	}

	const groups = getSubWorkflowGroups(taskDefinitions, subWorkflows);
	const workflowValues = getWorkflowLevelValues([
		...taskDefinitions,
		...groups.flatMap((group) => group.taskDefinitions),
	]);

	const fillWorkflowValues = (tasks: TaskDefinition[]) =>
		tasks
			.filter((task) => !isSubWorkflowTask(task))
			.map((task) =>
				fillEmptyInputs(
					task,
					isWorkflowSpecificAttribute,
					(input) => workflowValues[input.attributeName!] as SnowAttribute['value']
				)
			);

	const parentTasks = fillWorkflowValues(taskDefinitions);
	const subWorkflowGroups = groups.map((group) => ({
		...group,
		taskDefinitions: fillWorkflowValues(group.taskDefinitions),
	}));

	return {
		taskDefinitions: parentTasks,
		subWorkflowGroups,
		allTaskDefinitions: [
			...parentTasks,
			...subWorkflowGroups.flatMap((group) => group.taskDefinitions),
		],
	};
};

/** What belongs on the entity row of one sub-workflow. The caller adds the identity fields. */
export interface SubWorkflowEntity {
	workflowId: string;
	workflowDefinition?: WorkflowDefinition;
	entityData: Record<string, unknown>;
	taskMaps: EntityAttributes;
}

/**
 * Splits what the requester submitted into the parent entity and one entity per sub-workflow,
 * so each workflow in the tree has a row of its own to read at run time.
 *
 * - Each sub-workflow entity holds only what its own tasks declare: their workflow-level values
 *   and one map per task.
 * - The parent entity also carries every sub-workflow's task maps, as
 *   `[workflowId]: { [taskId]: values }`, because validation reads child tasks from the parent's
 *   row. It carries the workflow-level values of the whole tree, which the first step merged.
 *
 * Without sub-workflows the entity comes back as it is.
 */
export const buildSubWorkflowEntities = (
	workflowTreeTasks: WorkflowTreeTasks,
	entity: SNOWEntity
): { parentEntity: SNOWEntity; subWorkflowEntities: SubWorkflowEntity[] } => {
	if (!workflowTreeTasks.subWorkflowGroups.length) {
		return { parentEntity: entity, subWorkflowEntities: [] };
	}

	const subWorkflowEntities = workflowTreeTasks.subWorkflowGroups.map((group) => ({
		workflowId: group.workflowId,
		workflowDefinition: group.workflowDefinition,
		entityData: getWorkflowLevelValues(group.taskDefinitions, entity.EntityData),
		taskMaps: Object.fromEntries(
			group.taskDefinitions.map((task) => [task.taskId, entity[task.taskId!] ?? {}])
		),
	}));

	const subWorkflowTaskIds = new Set(subWorkflowEntities.flatMap(({ taskMaps }) => Object.keys(taskMaps)));
	const parentRecord = Object.fromEntries(
		Object.entries(entity).filter(([key]) => !subWorkflowTaskIds.has(key))
	);

	return {
		parentEntity: {
			...parentRecord,
			...Object.fromEntries(
				subWorkflowEntities.map(({ workflowId, taskMaps }) => [workflowId, taskMaps])
			),
			EntityData: {
				...entity.EntityData,
				...getWorkflowLevelValues(workflowTreeTasks.allTaskDefinitions, entity.EntityData),
			},
		} as SNOWEntity,
		subWorkflowEntities,
	};
};

/** How long to wait before each retry of a sub-workflow entity that failed to be created. */
export const SUB_WORKFLOW_ENTITY_RETRY_DELAYS_MS = [500, 1000];

/**
 * Calls `request` again after each delay while its response reports an error, and resolves to the
 * last response. Use it only for requests that are safe to send again.
 */
export const retryOnError = async <Response extends { error?: string } | undefined>(
	request: () => Promise<Response>,
	delaysMs: number[]
): Promise<Response> => {
	let response = await request();

	for (const delayMs of delaysMs) {
		if (!response?.error) break;

		await new Promise((resolve) => setTimeout(resolve, delayMs));
		response = await request();
	}

	return response;
};
